import React from "react";
import { injectIntl, IntlShape } from "react-intl";
import { gql, useMutation } from "@apollo/client";
import { styled, useTheme } from "@material-ui/styles";
import { Card, Chip, IconButton, Theme } from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/DeleteOutlineRounded";
import moment from "moment";
import { IStyledArguments } from "../../types/styled-arguments";
import ConfirmDialog from "../atoms/ConfirmDialog";
import { getExerciseTypeLabels } from "../pre-season-shared/constants";
import messages from "./PageTeamPreSeason.intl";

export const DELETE_PRE_SEASON_EXERCISE = gql`
  mutation DeletePreSeasonExercise($id: ID!) {
    deletePreSeasonExercise(id: $id) {
      op
      error
    }
  }
`;

interface IPreSeasonExercise {
  id: string;
  title: string;
  description?: string;
  type: string;
  startDate: string;
  endDate: string;
  weekdays: number[];
  totalEntries: number;
  completedEntries: number;
  notDoneEntries: number;
  players: Array<{ id: string }>;
}

const Row = styled(Card)(({ theme }: IStyledArguments) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(2, 2.5),
  marginBottom: theme.spacing(1.5),
  borderRadius: "16px",
  border: `1px solid ${theme.palette.extra.borderColor}`,
  cursor: "pointer",
  transition: "border-color 0.2s ease",
  "&:hover": {
    borderColor: theme.palette.primary.main,
  },
}));

const RowSelected = styled(Row)(({ theme }: IStyledArguments) => ({
  borderColor: theme.palette.primary.main,
}));

const Info = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: 4,
});

const TitleText = styled("div")({
  fontWeight: 700,
  fontSize: "1.05rem",
});

const MetaText = styled("div")(({ theme }: IStyledArguments) => ({
  fontSize: "0.85rem",
  color: theme.palette.text.secondary,
}));

interface IPreSeasonExerciseListProps {
  exercises: IPreSeasonExercise[];
  selectedId?: string;
  onSelect: (id: string) => void;
  onDeleted: () => void;
  intl: IntlShape;
}

const PreSeasonExerciseList = injectIntl<
  Omit<IPreSeasonExerciseListProps, "intl">
>(({ exercises, selectedId, onSelect, onDeleted, intl }) => {
  const theme = useTheme<Theme>();
  const [deleteExercise] = useMutation(DELETE_PRE_SEASON_EXERCISE);
  const typeLabels = getExerciseTypeLabels(intl);

  const handleDelete = async (id: string, confirmed: boolean) => {
    if (!confirmed) return;
    await deleteExercise({ variables: { id } });
    onDeleted();
  };

  return (
    <div>
      {exercises.map((exercise) => {
        const RowComponent = exercise.id === selectedId ? RowSelected : Row;
        return (
          <RowComponent
            key={exercise.id}
            onClick={() => onSelect(exercise.id)}
          >
            <Info>
              <TitleText>{exercise.title}</TitleText>
              <MetaText>
                {typeLabels[exercise.type as keyof typeof typeLabels]} ·{" "}
                {moment(exercise.startDate).format("DD/MM")} -{" "}
                {moment(exercise.endDate).format("DD/MM")} ·{" "}
                {exercise.players.length}{" "}
                {exercise.players.length === 1
                  ? intl.formatMessage(messages.playerSingular)
                  : intl.formatMessage(messages.playerPlural)}
              </MetaText>
            </Info>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Chip
                size="small"
                label={`${exercise.completedEntries}/${exercise.totalEntries}`}
                style={{
                  background: theme.palette.extra.accentSoft,
                  color: theme.palette.primary.dark,
                  fontWeight: 700,
                }}
              />
              <ConfirmDialog
                title={intl.formatMessage(messages.deleteConfirmTitle)}
                message={intl.formatMessage(messages.deleteConfirmMessage)}
                callback={(confirmed: boolean) =>
                  handleDelete(exercise.id, confirmed)
                }
                trigger={({ onOpen }: any) => (
                  <IconButton
                    size="small"
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      onOpen();
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              />
            </div>
          </RowComponent>
        );
      })}
    </div>
  );
});

export default PreSeasonExerciseList;
